import type { Plan, PlanId } from '../types/plan'

export const plans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 2,
    tagline: 'One site, both checks — or two sites watched for expiry.',
    services: 2,
    sslCadence: 'Daily',
    pingOptions: [30],
    recipientLimit: 1,
    warningWindowFixed: 14,
    warningWindowRange: null,
    historyLabel: '7 days',
    integrations: false,
    teamSeats: 1,
    statusPage: false,
    apiAccess: false,
    recommended: false,
    highlights: [
      '2 services',
      'Certificates checked daily',
      'Uptime pinged every 30 minutes',
      '1 email recipient',
      'Warning 14 days before expiry',
      '7 days of history',
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    monthlyPrice: 5,
    tagline: 'The everyday plan for a handful of sites you cannot lose.',
    services: 4,
    sslCadence: 'Daily',
    pingOptions: [30, 15, 5],
    recipientLimit: 3,
    warningWindowFixed: null,
    warningWindowRange: [1, 60],
    historyLabel: '30 days',
    integrations: false,
    teamSeats: 1,
    statusPage: false,
    apiAccess: false,
    recommended: false,
    highlights: [
      '4 services, any mix',
      'Certificates checked daily',
      'Uptime pinged every 5 minutes',
      'Up to 3 email recipients',
      'Warning window you choose (1–60 days)',
      '30 days of history',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 15,
    tagline: 'For agencies and teams answering for someone else’s uptime.',
    services: 15,
    sslCadence: 'Daily',
    pingOptions: [30, 15, 5, 1],
    recipientLimit: 'unlimited',
    warningWindowFixed: null,
    warningWindowRange: [1, 90],
    historyLabel: '1 year',
    integrations: true,
    teamSeats: 5,
    statusPage: true,
    apiAccess: true,
    recommended: true,
    highlights: [
      '15 services, any mix',
      'Uptime pinged every minute',
      'Unlimited email recipients',
      'Slack and webhook alerts',
      '5 team seats',
      '1 year of history, plus API access',
    ],
  },
]

export function getPlan(id: PlanId): Plan {
  const found = plans.find((plan) => plan.id === id)
  if (!found) {
    return plans[1]
  }
  return found
}

/** Two months free when paid yearly. */
export function annualPrice(plan: Plan): number {
  return plan.monthlyPrice * 10
}

export const comparisonRows: {
  label: string
  hint?: string
  value: (plan: Plan) => string
}[] = [
  {
    label: 'Services included',
    hint: 'One check on one domain = one service',
    value: (plan) => `${plan.services}`,
  },
  { label: 'SSL expiry checks', value: (plan) => plan.sslCadence },
  {
    label: 'Fastest ping interval',
    value: (plan) => {
      const fastest = plan.pingOptions[plan.pingOptions.length - 1]
      return fastest === 1 ? 'Every minute' : `Every ${fastest} minutes`
    },
  },
  {
    label: 'Email recipients',
    value: (plan) =>
      plan.recipientLimit === 'unlimited'
        ? 'Unlimited'
        : `${plan.recipientLimit}`,
  },
  {
    label: 'Expiry warning window',
    value: (plan) =>
      plan.warningWindowFixed
        ? `Fixed at ${plan.warningWindowFixed} days`
        : `${plan.warningWindowRange?.[0]}–${plan.warningWindowRange?.[1]} days`,
  },
  { label: 'History retained', value: (plan) => plan.historyLabel },
  {
    label: 'Slack & webhook alerts',
    value: (plan) => (plan.integrations ? 'Included' : '—'),
  },
  {
    label: 'Team seats',
    value: (plan) => (plan.teamSeats > 1 ? `${plan.teamSeats}` : '1'),
  },
  {
    label: 'Public status page',
    value: (plan) => (plan.statusPage ? 'Included' : '—'),
  },
  { label: 'API access', value: (plan) => (plan.apiAccess ? 'Included' : '—') },
]

export const pricingFaqs: { question: string; answer: string }[] = [
  {
    question: 'What exactly counts as a service?',
    answer:
      'One check on one domain. Watching the certificate on acme.com is one service; pinging acme.com for uptime is a second. A Standard plan with 4 services covers two domains completely, or four domains for expiry only — the mix is yours.',
  },
  {
    question: 'Can I mix SSL and uptime however I like?',
    answer:
      'Yes. Every domain you add has the two checks as independent switches. Turn on only what that site needs, and change it whenever you want.',
  },
  {
    question: 'What happens when I hit my service limit?',
    answer:
      'The check you are trying to switch on is disabled with a note telling you so, before you save anything. Nothing silently stops working and nothing gets billed as overage — you either free up a service or move up a plan.',
  },
  {
    question: 'What if my card fails?',
    answer:
      'We retry for seven days and email you each time. Monitoring keeps running throughout that window, because a lapsed card is a worse reason to miss an expiring certificate than no monitoring at all.',
  },
  {
    question: 'Can I change plans mid-month?',
    answer:
      'Any time. Upgrades apply immediately and are prorated. On a downgrade we show you exactly which monitors would exceed the smaller allowance and let you choose which ones to keep.',
  },
]
